<script>
import { getTemplateGroupedList } from "@/entities/template";
import { getDirectoryItem } from "@/entities/directory";
import { FormSheet } from "@/shared/ui/form-sheet";
import { Input } from "@/shared/ui/input";
import { Popover, PopoverTrigger, PopoverContent } from "@/shared/ui/popover";
import { Checkbox } from "@/shared/ui/checkbox";
import { Badge } from "@/shared/ui/badge";
import Button from "@/shared/ui/button/Button.vue";
import { Search } from "lucide-vue-next";
import { CATEGORY_CODES } from "@/shared/lib/constants";

export default {
  name: "CreateAction",
  components: {
    Button,
    FormSheet,
    Input,
    Popover,
    PopoverTrigger,
    PopoverContent,
    Checkbox,
    Badge,
    Search,
  },

  props: {
    title: {
      type: String,
      default: "",
    },
    predefinedCategories: {
      type: Array,
      default: () => [],
    },
    routeName: {
      type: String,
      default: "notarial_action_new",
    },
    disableRedirect: {
      type: Boolean,
      default: false,
    },
    inline: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["close", "select"],

  data() {
    return {
      visible: false,
      initialLoading: false,

      template_categories: [],

      categories: [],

      form: {
        reference_codes: [],
        template_name: null,
      },

      _searchTimer: null,
      showFilters: false,
      categorySearch: "",
    };
  },

  computed: {
    allowSoverExecutNadpis() {
      return (
        this.predefinedCategories?.includes(
          CATEGORY_CODES.SOVER_EXECUT_NADPIS
        ) || false
      );
    },
    availableCategories() {
      if (this.allowSoverExecutNadpis) return this.categories;
      return this.categories.filter(
        (item) => String(item.code) !== CATEGORY_CODES.SOVER_EXECUT_NADPIS
      );
    },
    filteredCategories() {
      if (this.allowSoverExecutNadpis) return this.template_categories;
      return this.template_categories.filter(
        (cat) => String(cat.code) !== CATEGORY_CODES.SOVER_EXECUT_NADPIS
      );
    },
    selectedCategoriesCount() {
      return this.form.reference_codes?.length || 0;
    },
    filteredCategoryOptions() {
      const q = this.categorySearch.trim().toLowerCase();
      if (!q) return this.availableCategories;
      return this.availableCategories.filter((item) =>
        (item.name || item.code || "").toString().toLowerCase().includes(q)
      );
    },
  },

  watch: {
    "form.reference_codes": {
      handler: "debouncedLoad",
      deep: true,
    },
    "form.template_name": "debouncedLoad",
    predefinedCategories: {
      handler(val) {
        this.form.reference_codes = Array.isArray(val) ? [...val] : [];
      },
      immediate: true,
    },
  },

  async created() {
    await this.getTemplateCategories();
    await this.loadGroupedTemplates();
  },

  mounted() {
    this.visible = true;
  },

  beforeUnmount() {
    clearTimeout(this._searchTimer);
  },

  methods: {
    async getTemplateCategories() {
      try {
        const data = await getDirectoryItem("notarial_action_category");

        if (data?.success && data.payload?.children) {
          this.categories = data.payload.children;
        }
      } catch (error) {
        console.error("Ошибка загрузки категорий:", error);
      }
    },

    debouncedLoad() {
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        this.loadGroupedTemplates();
      }, 500);
    },

    async loadGroupedTemplates() {
      this.initialLoading = true;

      try {
        const params = {};

        if (this.form.reference_codes?.length) {
          params.category_codes = this.form.reference_codes.join(",");
        }

        if (this.form.template_name) {
          params.q = this.form.template_name;
        }

        const res = await getTemplateGroupedList(params);

        if (!res?.success || !Array.isArray(res.payload)) {
          this.template_categories = [];
          return;
        }

        this.template_categories = res.payload
          .map((cat) => ({
            code: cat.code,
            name: cat.name || cat.name_kz || cat.code,
            templates: (cat.items || []).map((item) => ({
              code: item.code,
              name: item.name || item.name_kz || item.code,
            })),
          }))
          .filter((cat) => cat.templates.length > 0);
      } catch (err) {
        console.error("Ошибка загрузки шаблонов:", err);
        this.template_categories = [];
      } finally {
        this.initialLoading = false;
      }
    },

    createAction(code) {
      this.$emit("select", code);
      if (!this.disableRedirect) {
        this.$router.push({
          name: this.routeName,
          query: { code },
        });
      }
      this.handleClose();
    },

    handleClose() {
      this.visible = false;
      this.$emit("close");
    },
    toggleFilters() {
      this.showFilters = !this.showFilters;
    },
    isCategorySelected(code) {
      return this.form.reference_codes?.includes(code) || false;
    },
    toggleCategory(code) {
      const arr = this.form.reference_codes || [];
      const idx = arr.indexOf(code);
      if (idx === -1) {
        arr.push(code);
      } else {
        arr.splice(idx, 1);
      }
      this.form.reference_codes = [...arr];
    },
    clearCategories() {
      this.form.reference_codes = [];
    },
  },
};
</script>

<template>
  <FormSheet
    v-if="!inline"
    v-model:open="visible"
    :title="title || $t('Создать действие')"
    :width="500"
    :show-footer="false"
    @close="handleClose"
  >
    <div class="w-full h-full flex flex-col gap-6">
      <div class="w-full flex flex-col gap-2 flex-shrink-0">
        <div class="w-full flex gap-3">
          <div class="relative flex-1">
            <Search
              class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
            />
            <Input
              v-model="form.template_name"
              :placeholder="$t('Поиск по названию шаблона')"
              class="pl-9"
            />
          </div>
          <Popover>
            <PopoverTrigger as-child>
              <Button variant="outline" class="gap-2">
                <svg-icon
                  name="huge-filter"
                  class="w-4 h-4 text-muted-foreground"
                />
                <span class="text-muted-foreground text-xs">{{
                  $t("Категории")
                }}</span>
                <Badge v-if="selectedCategoriesCount > 0" variant="secondary">
                  {{ selectedCategoriesCount }}
                </Badge>
              </Button>
            </PopoverTrigger>
            <PopoverContent class="w-[260px] p-1.5">
              <div class="relative mb-1.5">
                <Search
                  class="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground"
                />
                <Input
                  v-model="categorySearch"
                  :placeholder="$t('Поиск категории')"
                  class="pl-7 h-8 text-sm"
                />
              </div>
              <div class="flex flex-col gap-0.5 max-h-[280px] overflow-y-auto">
                <label
                  v-for="item in filteredCategoryOptions"
                  :key="item.code"
                  class="flex items-center gap-2 px-1.5 py-1 rounded cursor-pointer hover:bg-muted"
                >
                  <Checkbox
                    size="small"
                    :model-value="isCategorySelected(String(item.code))"
                    @change="toggleCategory(String(item.code))"
                  />
                  <span class="text-sm">{{ item.name }}</span>
                </label>
                <div
                  v-if="!filteredCategoryOptions.length"
                  class="text-xs text-muted-foreground px-1.5 py-2 text-center"
                >
                  {{ $t("Ничего не найдено") }}
                </div>
              </div>
              <div
                v-if="selectedCategoriesCount > 0"
                class="border-t border-border mt-1.5 pt-1.5"
              >
                <button
                  type="button"
                  class="w-full text-xs text-muted-foreground hover:text-foreground px-1.5 py-1"
                  @click="clearCategories"
                >
                  {{ $t("Сбросить") }}
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <main class="flex-1 overflow-y-auto w-full flex flex-col gap-6 pb-4">
        <template v-if="initialLoading">
          <div v-for="n in 5" :key="n" class="group">
            <div class="h-4 bg-muted rounded-md w-1/3 mb-3 animate-pulse" />
            <ul>
              <li v-for="m in 3" :key="m" class="animate-pulse">
                <div class="h-4 bg-muted rounded-md w-2/3" />
              </li>
            </ul>
          </div>
        </template>

        <template v-else>
          <div
            v-if="!filteredCategories.length"
            class="text-center text-muted-foreground py-10"
          >
            {{ $t("Ничего не найдено") }}
          </div>

          <div v-for="cat in filteredCategories" :key="cat.code" class="group">
            <div class="text-muted-foreground text-sm font-medium leading-5">
              {{ cat.name }}
            </div>

            <ul v-if="cat.templates.length">
              <li
                v-for="action in cat.templates"
                :key="action.code"
                class="cursor-pointer"
                @click="createAction(action.code)"
              >
                <div class="w-full flex justify-between items-center">
                  <div
                    class="flex-1 text-foreground text-sm font-medium leading-5 line-clamp-2"
                  >
                    {{ action.name }}
                  </div>
                  <svg-icon
                    name="huge-chevron-right"
                    class="w-4 h-4 flex-shrink-0"
                  />
                </div>
              </li>
            </ul>

            <div v-else class="text-muted-foreground text-sm px-1">
              {{ $t("Нет шаблонов") }}
            </div>
          </div>
        </template>
      </main>
    </div>
  </FormSheet>

  <div v-else class="w-full h-full flex flex-col gap-6">
    <div class="w-full flex flex-col gap-2 flex-shrink-0">
      <div class="w-full flex gap-3">
        <div class="relative flex-1">
          <Search
            class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
          />
          <Input
            v-model="form.template_name"
            :placeholder="$t('Поиск по названию шаблона')"
            class="pl-9"
          />
        </div>
        <Popover>
          <PopoverTrigger as-child>
            <Button variant="outline" class="gap-2">
              <svg-icon
                name="huge-filter"
                class="w-4 h-4 text-muted-foreground"
              />
              <span class="text-muted-foreground text-xs">{{
                $t("Категории")
              }}</span>
              <Badge v-if="selectedCategoriesCount > 0" variant="secondary">
                {{ selectedCategoriesCount }}
              </Badge>
            </Button>
          </PopoverTrigger>
          <PopoverContent class="w-[260px] p-1.5">
            <div class="relative mb-1.5">
              <Search
                class="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground"
              />
              <Input
                v-model="categorySearch"
                :placeholder="$t('Поиск категории')"
                class="pl-7 h-8 text-sm"
              />
            </div>
            <div class="flex flex-col gap-0.5 max-h-[280px] overflow-y-auto">
              <label
                v-for="item in filteredCategoryOptions"
                :key="item.code"
                class="flex items-center gap-2 px-1.5 py-1 rounded cursor-pointer hover:bg-muted"
              >
                <Checkbox
                  size="small"
                  :model-value="isCategorySelected(String(item.code))"
                  @change="toggleCategory(String(item.code))"
                />
                <span class="text-sm">{{ item.name }}</span>
              </label>
              <div
                v-if="!filteredCategoryOptions.length"
                class="text-xs text-muted-foreground px-1.5 py-2 text-center"
              >
                {{ $t("Ничего не найдено") }}
              </div>
            </div>
            <div
              v-if="selectedCategoriesCount > 0"
              class="border-t border-border mt-1.5 pt-1.5"
            >
              <button
                type="button"
                class="w-full text-xs text-muted-foreground hover:text-foreground px-1.5 py-1"
                @click="clearCategories"
              >
                {{ $t("Сбросить") }}
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>

    <main class="flex-1 overflow-y-auto w-full flex flex-col gap-6 pb-4">
      <template v-if="initialLoading">
        <div v-for="n in 5" :key="n" class="group">
          <div class="h-4 bg-muted rounded-md w-1/3 mb-3 animate-pulse" />
          <ul>
            <li v-for="m in 3" :key="m" class="animate-pulse">
              <div class="h-4 bg-muted rounded-md w-2/3" />
            </li>
          </ul>
        </div>
      </template>

      <template v-else>
        <div
          v-if="!filteredCategories.length"
          class="text-center text-muted-foreground py-10"
        >
          {{ $t("Ничего не найдено") }}
        </div>

        <div v-for="cat in filteredCategories" :key="cat.code" class="group">
          <div class="text-muted-foreground text-sm font-medium leading-5">
            {{ cat.name }}
          </div>

          <ul v-if="cat.templates.length">
            <li
              v-for="action in cat.templates"
              :key="action.code"
              class="cursor-pointer"
              @click="createAction(action.code)"
            >
              <div class="w-full flex justify-between items-center">
                <div
                  class="flex-1 text-foreground text-sm font-medium leading-5 line-clamp-2"
                >
                  {{ action.name }}
                </div>
                <svg-icon
                  name="huge-chevron-right"
                  class="w-4 h-4 flex-shrink-0"
                />
              </div>
            </li>
          </ul>

          <div v-else class="text-muted-foreground text-sm px-1">
            {{ $t("Нет шаблонов") }}
          </div>
        </div>
      </template>
    </main>
  </div>
</template>

<style scoped lang="scss">
.group {
  @apply w-full flex flex-col justify-start items-start gap-4;

  ul {
    @apply w-full border border-solid border-border flex flex-col justify-start items-start overflow-hidden;
    border-radius: var(--radius);

    li {
      @apply w-full px-4 py-3 bg-card border-b border-border flex flex-col justify-center items-start gap-4;

      &:last-child {
        @apply border-b-0;
      }

      &:hover {
        @apply bg-muted;
      }
    }
  }
}

.filter-hidden {
  display: none;
}

.filter-icon {
  @apply p-2 rounded-md border border-solid border-border bg-card cursor-pointer flex items-center justify-center;
  &:hover {
    @apply bg-muted;
  }
}

.filter-button {
  @apply px-3 py-2.5 bg-card rounded-xl outline outline-1 outline-border;
  @apply flex items-center gap-3 text-sm text-foreground;
  display: none;
}
</style>
